import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { getFlotiqMainPageUrl, getTestProps } from '../lib/helpers';

import { defaultButtonsProps, useModals } from '../contexts/ModalContext';

import {
  CaretRightIcon,
  DocumentIcon,
  TemplateIcon,
  UsersCurrentColorIcon,
} from '../images/shapes';

import { useUserSpaces } from './api/useUserSpaces';
import useSpaceSynchronization from './synchronization/useSpaceSynchronization';
import useSelectedSpace from './useSelectedSpace';

const MODAL_ID = 'import-space-modal';

export const useImportSpace = (testId?: string) => {
  const { t } = useTranslation();
  const modal = useModals();

  const { space, spaceData } = useSelectedSpace();
  const { spaces } = useUserSpaces();

  const { handleImport, handleSynchronize } = useSpaceSynchronization(space);

  const isSyncAvailable = !!spaces && spaces.length > 1;

  const options = [
    {
      key: 'import-zip',
      title: t('SpaceSynchronization.Import.ImportButton'),
      description: t('SpaceSynchronization.Import.ImportDescription'),
      label: t('SpaceSynchronization.Import.ImportButton'),
      onClick: async () => {
        const result = await handleImport('importSpace', spaceData?.name);
        if (result) {
          modal.close(MODAL_ID);
        }
      },
      icon: <DocumentIcon className="size-6" />,
    },
    ...(isSyncAvailable
      ? [
          {
            key: 'sync-spaces',
            title: t(
              'SpaceSynchronization.Synchronize.SynchronizeWithAnotherSpace',
            ),
            description: t(
              'SpaceSynchronization.Synchronize.SynchronizeWithAnotherSpaceDescription',
            ),
            label: t('SpaceSynchronization.Synchronize.SynchronizeSpace'),
            onClick: async () => {
              const result = await handleSynchronize();
              if (result) {
                modal.close(MODAL_ID);
              }
            },
            icon: <UsersCurrentColorIcon className="size-6" />,
          },
        ]
      : []),
    {
      key: 'import-from-wordpress',
      title: t('SpaceSynchronization.Import.ImportFromWordpress'),
      description: t(
        'SpaceSynchronization.Import.ImportFromWordpressDescription',
      ),
      label: t('SpaceSynchronization.Import.ImportFromWordpressLearnMore'),
      link: getFlotiqMainPageUrl('/docs/CLI/wordpress-importer/?h=wordpress'),
      icon: <TemplateIcon className="size-6" />,
    },
  ];

  const openImportModal = () => {
    modal({
      id: MODAL_ID,
      content: (
        <>
          <h2
            className="text-3xl font-bold text-center mb-4"
            {...getTestProps(testId, 'title')}
          >
            {t('Home.ImportData')}
          </h2>
          <p className="text-center mb-8">{t('Home.ImportDataDescription')}</p>
          <div
            className={twMerge(
              'grid grid-cols-3 gap-6',
              isSyncAvailable ? 'grid-cols-3' : 'grid-cols-2',
            )}
          >
            {options.map((option) => {
              const Component = option.link ? 'a' : 'button';
              return (
                <Component
                  key={option.key}
                  className={
                    'flex h-full flex-col rounded-lg border hover:border-blue p-4 pb-7 gap-3 ' +
                    'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 ' +
                    'group transition-colors focus:outline-none focus-visible:border-blue dark:text-white ' +
                    'text-left cursor-pointer'
                  }
                  onClick={option.onClick}
                  {...(option.link && {
                    href: option.link,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  })}
                  {...getTestProps(testId, option.key)}
                >
                  <div
                    className={
                      'flex size-12 items-center justify-center rounded-md ' +
                      'bg-blue-50 dark:bg-blue text-blue dark:text-white'
                    }
                    aria-hidden="true"
                  >
                    {option.icon}
                  </div>
                  <h3 className="text-lg font-bold">{option.title}</h3>
                  <p className="text-lg">{option.description}</p>
                  <span className="mt-auto text-blue dark:text-white group-hover:underline">
                    {option.label}
                    <CaretRightIcon className="inline size-2.5 ml-2" />
                  </span>
                </Component>
              );
            })}
          </div>
        </>
      ),
      size: isSyncAvailable ? '3xl' : 'xl',
      buttons: [
        {
          ...defaultButtonsProps.cancel,
          label: t('Global.Cancel'),
        },
      ],
    });
  };
  return { openImportModal };
};
